import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CountryName } from 'src/assets/functions/get-country-name';
import { maskCpfAndCnpj, maskPhoneNumber, maskZipCode } from 'src/assets/functions/information-masks';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractCancel } from '../../models/contract/contract-cancel';
import { ContractEdit } from '../../models/contract/contract-edit';
import { ContractEditCancel } from '../../models/contract/contract-edit-cancel';
import { FilterContractSearchResult } from '../../models/contract/filter-contract-search-result';
import { SalesTypeFordEnum } from '../../models/enum/sales-type-ford';
import { StatusContract } from '../../models/enum/status-contract';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ContractService } from '../../services/contract/contract.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';

@Component({
	selector: 'app-contract-edit-dealer',
	templateUrl: './contract-edit-dealer.component.html',
	styleUrls: ['./contract-edit-dealer.component.css'],
	standalone: false
})
export class ContractEditDealerComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly searchContext = 'dealer' as const;

	pt = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
		dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
		dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
		monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
			'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
		monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
		today: 'Hoje',
		clear: 'Limpar'
	};

	es = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sábado'],
		dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
		dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
		monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
			'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
		monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
		today: 'Hoy',
		clear: 'Límpar'
	};

	calendarLang = this.pt;

	statusContract = [
		{ label: 'TRANSLATE.SELECT', value: 0 },
		{ label: 'TRANSLATE.ACTIVE', value: 5 },
		{ label: 'TRANSLATE.BLOCKED', value: 10 },
		{ label: 'TRANSLATE.CANCELED', value: 15 },
		{ label: 'TRANSLATE.FINISHED', value: 20 },
		{ label: 'TRANSLATE.CREATED', value: 25 },
		{ label: 'TRANSLATE.PENDING.PAYMENT', value: 30 },
		{ label: 'TRANSLATE.PENDING.CANCELLATION', value: 55 },
		{ label: 'TRANSLATE.PENDING.ACTIVATION', value: 60 },
		{ label: 'TRANSLATE.PENDING.AMENDMENT', value: 65 },
		{ label: 'TRANSLATE.WITH.ERROR', value: 95 }
	];

	filterContractSearchResult: FilterContractSearchResult;
	isPermissionButtonEditDisabled = true;
	isEditDisabled = true;
	isButtonSaveDisabled = true;
	isButtonEnviarLinkDisabled = true;
	isEditBlockDisabled = true;
	editedCancellationInformation = false;
	countCaracteresTextArea = 0;
	contractEdit: ContractEdit = {} as ContractEdit;
	isSubmitted = false;
	hasAttemptedSave = false;
	blockedUi = false;
	isEditingMode = false;
	salesTypeFord: string = '';
	validateEmail = true;
	userCountry: string = '';
	canceledContractInformation: ContractCancel | null = {} as ContractCancel;
	cancellationDate: Date | null = new Date();
	systemCountry: string = '';
	cancelledTotalValueEdit: number | null = 0;
	cancelledFordValueEdit: number | null = 0;
	cancelledDealerValueEdit: number | null = 0;
	editedContractCancel: ContractEditCancel = {} as ContractEditCancel;
	totalValueObligatory = false;
	fordValueObligatory = false;
	dealerValueObligatory = false;
	isCancellationRecordMissing = false;
	isValidatingCancellationRecord = false;
	isResolvingEditEligibility = false;
	editingCustomerEmail: string = '';
	editingContractRemarks: string = '';
	contractCountryCode: string = '';
	requiredCustomerEmail = false;
	invalidEmailFormat = false;
	requiredContractNotes = false;
	editingContractInformation = false;

	private shouldApplyBrazilMasks(): boolean {
		return this.contractCountryCode === ConstantsFordProtect.BRA;
	}

	// Handlers for masked fields
	onDocumentInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.customerDocument = maskCpfAndCnpj(value || '');
		} else {
			this.filterContractSearchResult.customerDocument = value || '';
		}
	}

	onPhoneInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.customerPhone = maskPhoneNumber(value || '');
		} else {
			this.filterContractSearchResult.customerPhone = value || '';
		}
	}

	onCepInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.customerCep = maskZipCode(value || '');
		} else {
			this.filterContractSearchResult.customerCep = value || '';
		}
	}

	onDealerPhoneInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.dnPhone = maskPhoneNumber(value || '');
		} else {
			this.filterContractSearchResult.dnPhone = value || '';
		}
	}

	onSellerPhoneInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.sellerPhone = maskPhoneNumber(value || '');
		} else {
			this.filterContractSearchResult.sellerPhone = value || '';
		}
	}

	constructor(
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private apsPermissionService: ApsPermissionService,
		private contractService: ContractService,
		private systemCountryService: SystemCountryService,
		private countryName: CountryName,
	) {

		this.userCountry = apsPermissionService.getUserCountry();
		this.filterContractSearchResult = new FilterContractSearchResult();
		this.systemCountryService.setSystemCountry();
	}

	async ngOnInit() {

		window.scrollTo(0, 0);
		await this.apsPermissionService.bootstrapPermissions();

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractView);

		this.isPermissionButtonEditDisabled = this.apsPermissionService
			.isPermissionValidationDisabled(ApsPermitedScreen.screenContractEdit);
		this.searchContract();

		this.updateCalendarLanguage(this.translateService.currentLang || this.translateService.defaultLang || ConstantsFordProtect.PT_BR);

		this.translateService.onLangChange.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe((langChange: LangChangeEvent) => {
			this.updateCalendarLanguage(langChange.lang);
		});
	}

	private updateCalendarLanguage(language: string): void {
		this.calendarLang = language.toLowerCase().startsWith('es') ? this.es : this.pt;
		this.searchSalesTypeFord();
	}

	private searchContract() {
		const savedSearchResult = this.contractService.getFilterContractSearchResultSimplifiedByContext(this.searchContext);

		if (savedSearchResult !== null && savedSearchResult !== undefined) {

			const contractId = savedSearchResult.contractKey;
			const productId = savedSearchResult.productKey;
			const coverageCategoryId = savedSearchResult.coverageCategoryKey;
			const vin = savedSearchResult.vin;

			this.searchContractId(contractId, productId, coverageCategoryId, vin);
		}
	}

	private convertDateNeverTimezone(date: Date): Date {
		return new Date(date + 'T00:00:00.00');
	}

	textAreaCounterCaracteres() {
		if (this.editingContractRemarks === null || this.editingContractRemarks === '') {
			this.countCaracteresTextArea = 0;
		} else {
			this.countCaracteresTextArea = this.editingContractRemarks.length;
		}
	}

	buttonComeBack() {
		this.router.navigate(['contract-search-dealer'], { state: { restoreSearch: true, source: 'contract-edit-dealer' } });
		this.canceledContractInformation = new ContractCancel();
	}

	buttonEditEnabled() {
		if (this.isCanceledContract()) {
			this.showCanceledContractCannotBeEditedMessage();
			return;
		}

		if (this.isCanceledContractWithoutEditableCancellation()) {
			this.showCanceledContractNotEditableMessage();
			return;
		}

		this.isEditingMode = true;
		this.hasAttemptedSave = false;
		this.clearRequiredFields();
		this.isEditDisabled = false;
		this.isButtonSaveDisabled = false;
	}

	buttonEditDisabled() {
		this.isEditingMode = false;
		this.hasAttemptedSave = false;
		this.isEditDisabled = true;
		this.isButtonSaveDisabled = true;
		this.clearRequiredFields();
		this.searchContract();
	}

	get isEditActionDisabledByCancellationState(): boolean {
		return this.isResolvingEditEligibility || this.isCanceledContract() || this.isCanceledContractWithoutEditableCancellation();
	}

	get editDisabledReasonKey(): string {
		if (this.isResolvingEditEligibility) {
			return '';
		}

		if (this.isCanceledContract()) {
			return 'TRANSLATE.CONTRACT.CANCELED.CANNOT.BE.EDITED';
		}

		if (this.isCanceledContractWithoutEditableCancellation()) {
			return 'TRANSLATE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION';
		}

		return '';
	}

	private isCanceledContract(): boolean {
		return this.filterContractSearchResult?.contractStatusCode === StatusContract.CANCELED;
	}

	private isCanceledContractWithoutEditableCancellation(): boolean {
		return this.filterContractSearchResult.contractStatusCode === StatusContract.CANCELED
			&& this.isCancellationRecordMissing;
	}

	private showCanceledContractNotEditableMessage(): void {
		this.messageService.add({
			severity: 'warn',
			summary: this.translateService.instant('TRANSLATE.ALERT'),
			detail: this.translateService.instant('TRANSLATE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION')
		});
	}

	private showCanceledContractCannotBeEditedMessage(): void {
		this.messageService.add({
			severity: 'warn',
			summary: this.translateService.instant('TRANSLATE.ALERT'),
			detail: this.translateService.instant('TRANSLATE.CONTRACT.CANCELED.CANNOT.BE.EDITED')
		});
	}

	private isMissingContractVinCancelError(err: unknown): boolean {
		const error = err as {
			status?: number;
			error?: { exceptionMessage?: string; message?: string };
			message?: string;
		};

		const message = `${error?.error?.exceptionMessage ?? ''} ${error?.error?.message ?? ''} ${error?.message ?? ''}`.toLowerCase();
		const hasExplicitContractVinCancelSignal =
			message.includes('contractvincancelnotfoundexception') || message.includes('contractvincancel');

		const hasCanceledContextWithoutEditableRecord =
			error?.status === 404
			&& this.isCancellationRecordMissing
			&& this.filterContractSearchResult?.productCode !== ConstantsFordProtect.PREB
			&& (
				this.filterContractSearchResult?.contractStatusCode === StatusContract.CANCELED
				|| this.filterContractSearchResult?.contractStatusCode === StatusContract.PENDING_CANCELLATION
			);

		return error?.status === 404 && (hasExplicitContractVinCancelSignal || hasCanceledContextWithoutEditableRecord);
	}

	buttonEnviarEmailDisabled() {
		this.isButtonEnviarLinkDisabled = true;
	}

	checkCustomerEmail() {

		this.requiredCustomerEmail = !this.editingCustomerEmail;
	}

	onCustomerEmailInput(value: string): void {
		this.editingCustomerEmail = value ?? '';

		// Clear invalidEmailFormat flag when email becomes valid
		if (this.editingCustomerEmail && this.isValidEmailFormat(this.editingCustomerEmail)) {
			this.invalidEmailFormat = false;
		}

		if (this.hasAttemptedSave) {
			this.requiredCustomerEmail = this.editingCustomerEmail.trim().length === 0;
		}
	}

	onContractNotesInput(value: string): void {
		this.editingContractRemarks = value ?? '';
		this.textAreaCounterCaracteres();

		// Clear invalidEmailFormat when user corrects email first
		if (this.invalidEmailFormat && this.editingCustomerEmail && this.isValidEmailFormat(this.editingCustomerEmail)) {
			this.invalidEmailFormat = false;
		}

		if (this.hasAttemptedSave) {
			this.requiredContractNotes = this.editingContractRemarks.trim().length === 0;
		}
	}

	buttonSaveEdit() {
		this.hasAttemptedSave = true;

		// Check email format FIRST (highest priority)
		if (this.editingCustomerEmail && !this.isValidEmailFormat(this.editingCustomerEmail)) {
			this.invalidEmailFormat = true;
			this.focusFirstInvalidRequiredField();
			return;
		}

		if (this.isCanceledContract()) {
			this.showCanceledContractCannotBeEditedMessage();
			return;
		}

		if (this.isCanceledContractWithoutEditableCancellation()) {
			this.showCanceledContractNotEditableMessage();
			return;
		}

		if (
			this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
			this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '' ||
			this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
			this.editingContractRemarks.trim().length === 0
		) {

			this.requiredCustomerEmail = this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
				this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '';
			this.requiredContractNotes = this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
				this.editingContractRemarks.trim().length === 0;
			this.focusFirstInvalidRequiredField();
			return;
		} else {

			this.clearRequiredFields();

			this.editingContractInformation =
				this.editingCustomerEmail !== this.filterContractSearchResult.customerEmail ||
				this.editingContractRemarks !== this.filterContractSearchResult.contractRemarks;

			this.savingContractChanges(this.createObjectContractEdit());
		}

	}

	private focusFirstInvalidRequiredField(): void {
		const invalidFieldIds: string[] = [];

		// Check invalidEmailFormat as FIRST priority
		if (this.invalidEmailFormat) {
			invalidFieldIds.push('customerEmailEdit');
		}

		if (this.requiredCustomerEmail) {
			invalidFieldIds.push('customerEmailEdit');
		}

		if (this.requiredContractNotes) {
			invalidFieldIds.push('contractNotesEdit');
		}

		if (!invalidFieldIds.length) {
			return;
		}

		const firstInvalidField = document.getElementById(invalidFieldIds[0]);
		if (!firstInvalidField) {
			return;
		}

		firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
		setTimeout(() => firstInvalidField.focus(), 200);
	}

	savingContractChanges(contractEdit: ContractEdit) {

		this.isSubmitted = true;
		this.blockedUi = true;
		contractEdit.country = this.countryName.getCodeFromName(this.filterContractSearchResult.country);
		contractEdit.email = this.editingCustomerEmail;
		contractEdit.contractRemarks = this.editingContractRemarks;
		console.log(this.cancelledTotalValueEdit);
		if (contractEdit.cancelledTotalValue == undefined) {
			contractEdit.cancelledTotalValue = 0;
		}

		this.contractService.contractEditPost(contractEdit)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: boolean) => {

				if (response) {

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.CONTRACT.SUCCESSFULLY.SAVED')
					});

					this.searchContract();
					this.blockedUi = false;
					this.isSubmitted = false;
					this.buttonEditDisabled();
				}

			}, (err) => {
				if (this.isMissingContractVinCancelError(err)) {
					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ALERT'),
						detail: this.translateService.instant('TRANSLATE.CANNOT.SAVE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION')
					});
					this.isSubmitted = false;
					this.blockedUi = false;
					return;
				}

				switch (err.status) {

					case 400:
					case 404:
					case 500:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.CONTRACT.CANNOT.BE.SAVE.CONTACT.ADM')
						});
						break;

					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

				this.isSubmitted = false;
				this.blockedUi = false;
			});

	}

	buttonSendEmailEnabled() {
		const today = new Date();
		if ((this.filterContractSearchResult.dateExpirationLink > today) && (this.filterContractSearchResult.status === 30)) {
			this.isButtonEnviarLinkDisabled = false;
		}
	}

	buttonSendEmail() {
		this.isSubmitted = true;
		this.blockedUi = true;

		this.contractService.sendPaymentLinkByEmail(this.filterContractSearchResult.contractCodeFp2)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: boolean) => {
				if (response) {
					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.EMAIL.SENT.SUCCESSFULLY')
					});
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.UNABLE.TO.SEND.EMAIL')
					});
				}

				this.searchContract();

				this.blockedUi = false;
				this.isSubmitted = false;
				this.buttonEnviarEmailDisabled();
			}, (err) => {
				if (err.status === 400 || err.status === 404 || err.status === 500) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.UNABLE.TO.SEND.EMAIL.CONTACT.ADM')
					});

				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

				this.buttonEnviarEmailDisabled();
				this.isSubmitted = false;
				this.blockedUi = false;
			});

	}

	private createObjectContractEdit(): ContractEdit {

		this.contractEdit = new ContractEdit();
		this.contractEdit.csId = this.apsPermissionService.getUserCsid();
		this.contractEdit.contractCodeFp2 = this.filterContractSearchResult.contractCodeFp2;
		this.contractEdit.contractRemarks = this.filterContractSearchResult.contractRemarks;
		this.contractEdit.contractStatusCode = this.filterContractSearchResult.contractStatusCode;
		this.contractEdit.country = this.filterContractSearchResult.country;
		this.contractEdit.coverageCategory = this.filterContractSearchResult.coverageCategory;
		this.contractEdit.coverageCode = this.filterContractSearchResult.coverageCode;
		this.contractEdit.coverageEndDate = this.filterContractSearchResult.coverageEndDate;
		this.contractEdit.operation = this.filterContractSearchResult.operation;
		this.contractEdit.vin = this.filterContractSearchResult.vin;
		this.contractEdit.vinKey = this.filterContractSearchResult.vinKey;
		this.contractEdit.coverageKey = this.filterContractSearchResult.coverageKey;
		this.contractEdit.productCode = this.filterContractSearchResult.productCode;
		this.contractEdit.productKey = this.filterContractSearchResult.productKey;
		this.contractEdit.email = this.filterContractSearchResult.customerEmail;
		this.contractEdit.coverageStartDate = this.filterContractSearchResult.coverageStartDate;
		this.contractEdit.coverageStartVisit = this.filterContractSearchResult.coverageStartVisit;
		this.contractEdit.cvvgDate = this.filterContractSearchResult.wsd;

		if (this.filterContractSearchResult.coverageKm !== null && this.filterContractSearchResult.coverageKm >= 0) {
			this.contractEdit.coverageKm = this.filterContractSearchResult.coverageKm;
		}

		if (this.filterContractSearchResult.coverageVisitCounter !== null && this.filterContractSearchResult.coverageVisitCounter >= 0) {
			this.contractEdit.coverageVisitCounter = this.filterContractSearchResult.coverageVisitCounter;
		}

		return this.contractEdit;
	}

	clearRequiredFields() {

		this.requiredCustomerEmail = false;
		this.requiredContractNotes = false;
	}

	private searchContractId(contractId: number, productId: number, coverageCategoryId: number, vin: string) {
		this.isCancellationRecordMissing = false;
		this.isValidatingCancellationRecord = false;
		this.isResolvingEditEligibility = true;

		this.contractService.searchContractId(contractId, productId, coverageCategoryId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: FilterContractSearchResult) => {

				this.filterContractSearchResult = response;
				this.contractCountryCode = response.country || '';
				// Apply masks to loaded data only for Brazil (BRA)
				if (this.shouldApplyBrazilMasks()) {
					this.filterContractSearchResult.customerDocument = maskCpfAndCnpj(this.filterContractSearchResult.customerDocument || '');
					this.filterContractSearchResult.customerPhone = maskPhoneNumber(this.filterContractSearchResult.customerPhone || '');
					this.filterContractSearchResult.customerCep = maskZipCode(this.filterContractSearchResult.customerCep || '');
					this.filterContractSearchResult.dnPhone = maskPhoneNumber(this.filterContractSearchResult.dnPhone || '');
					this.filterContractSearchResult.sellerPhone = maskPhoneNumber(this.filterContractSearchResult.sellerPhone || '');
				}

				this.filterContractSearchResult.country = this.countryName.getCountryName(this.filterContractSearchResult.country);
				this.editingCustomerEmail = this.filterContractSearchResult.customerEmail;
				this.editingContractRemarks = this.filterContractSearchResult.contractRemarks;

				this.textAreaCounterCaracteres();
				this.validationDate();
				this.searchSalesTypeFord();
				this.changeDecimalPoint();
				this.paymentMethodText();
				this.buttonSendEmailEnabled();

				if (this.filterContractSearchResult.productCode !== ConstantsFordProtect.PREB &&
					(this.filterContractSearchResult.contractStatusCode === StatusContract.CANCELED ||
						this.filterContractSearchResult.contractStatusCode === StatusContract.PENDING_CANCELLATION)) {

					this.isValidatingCancellationRecord = true;
					console.log(`Status do contrato é ${this.filterContractSearchResult.status}. Retornando o cancelamento do contrato`);
					this.getContractVinCancel(contractId, vin);
				} else {
					this.isResolvingEditEligibility = false;
				}
			}, (err) => {

				this.isValidatingCancellationRecord = false;
				this.isResolvingEditEligibility = false;

				switch (err.status) {

					case 400:
					case 404:
					case 500:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.INVALID.CONTRACT.SEARCH.CONTACT.ADM')
						});
						break;
					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;
					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});
	}

	private getContractVinCancel(contractId: number, vin: string) {

		this.contractService.getContractVinCancel(contractId, vin)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ContractCancel) => {

				this.canceledContractInformation = response;

				if (this.canceledContractInformation && Object.keys(this.canceledContractInformation).length > 0) {

					this.cancellationDate = this.filterContractSearchResult.vinActualEndDate;
					this.cancelledTotalValueEdit = this.canceledContractInformation.cancelledTotalValue;
					this.cancelledFordValueEdit = this.canceledContractInformation.cancelledFordValue;
					this.cancelledDealerValueEdit = this.canceledContractInformation.cancelledDealerValue;
				} else {

					// No result found, just clear the related fields
					this.cancellationDate = null;
					this.cancelledTotalValueEdit = null;
					this.cancelledFordValueEdit = null;
					this.cancelledDealerValueEdit = null;
				}

				this.isValidatingCancellationRecord = false;
				this.isResolvingEditEligibility = false;

			}, (err) => {

				if (err.status === 404) {
					this.isCancellationRecordMissing = true;
					this.canceledContractInformation = null;
					this.cancellationDate = null;
					this.cancelledTotalValueEdit = null;
					this.cancelledFordValueEdit = null;
					this.cancelledDealerValueEdit = null;
					this.isValidatingCancellationRecord = false;
					this.isResolvingEditEligibility = false;
					return;
				}

				this.isValidatingCancellationRecord = false;
				this.isResolvingEditEligibility = false;

				switch (err.status) {

					case 400:
					case 500:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.SEARCH.FOR.INVALID.CANCELED.CONTRACT')
						});
						break;
					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});

	}

	private validationDate() {

		if (this.filterContractSearchResult.contractDateCreate !== null) {
			this.filterContractSearchResult.contractDateCreate = this.convertDateNeverTimezone(this.filterContractSearchResult.contractDateCreate);
		}

		if (this.filterContractSearchResult.contractDateEnd !== null) {
			this.filterContractSearchResult.contractDateEnd = this.convertDateNeverTimezone(this.filterContractSearchResult.contractDateEnd);
		}

		if (this.filterContractSearchResult.contractDateUpdate !== null) {
			this.filterContractSearchResult.contractDateUpdate = this.convertDateNeverTimezone(this.filterContractSearchResult.contractDateUpdate);
		}

		if (this.filterContractSearchResult.wsd !== null) {
			this.filterContractSearchResult.wsd = this.convertDateNeverTimezone(this.filterContractSearchResult.wsd);
		}

		if (this.filterContractSearchResult.warrantyEndDate !== null) {
			this.filterContractSearchResult.warrantyEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.warrantyEndDate);
		}

		if (this.filterContractSearchResult.vinUpdateDate !== null) {
			this.filterContractSearchResult.vinUpdateDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinUpdateDate);
		}

		if (this.filterContractSearchResult.vinSaleDate !== null) {
			this.filterContractSearchResult.vinSaleDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinSaleDate);
		}

		if (this.filterContractSearchResult.vinStartDate !== null) {
			this.filterContractSearchResult.vinStartDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinStartDate);
		}

		if (this.filterContractSearchResult.vinEndDate !== null) {
			this.filterContractSearchResult.vinEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinEndDate);
		}

		if (this.filterContractSearchResult.coverageStartDate !== null) {
			this.filterContractSearchResult.coverageStartDate = this.convertDateNeverTimezone(this.filterContractSearchResult.coverageStartDate);
		}

		if (this.filterContractSearchResult.coverageEndDate !== null) {
			this.filterContractSearchResult.coverageEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.coverageEndDate);
		}

		if (this.filterContractSearchResult.coverageUpdateDate !== null) {
			this.filterContractSearchResult.coverageUpdateDate = this.convertDateNeverTimezone(this.filterContractSearchResult.coverageUpdateDate);
		}

		if (this.filterContractSearchResult.vinActualEndDate !== null) {
			this.filterContractSearchResult.vinActualEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinActualEndDate);
		}

		if (this.filterContractSearchResult.dateExpirationLink !== null) {
			this.filterContractSearchResult.dateExpirationLink = this.convertDateNeverTimezone(this.filterContractSearchResult.dateExpirationLink);
		}

	}

	rangeDateLabel(): string {

		const date = new Date();
		const dateLabelYear = date.getFullYear() + 50;
		return '1970:' + dateLabelYear;
	}

	searchSalesTypeFord() {
		this.salesTypeFord = this.translateSalesTypeFords(String(this.filterContractSearchResult.contractTypeCode));
	}

	private translateSalesTypeFords(saleType: string) {

		switch (saleType) {

			case SalesTypeFordEnum.RETAIL_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.RETAIL');
				break;

			case SalesTypeFordEnum.DIRECT_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.DIRECT');
				break;

			case SalesTypeFordEnum.GOVERNMENT_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.FLEET');
				break;

			case SalesTypeFordEnum.FREE_CONTRACT_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.FREE.CONTRACT');
				break;

			case SalesTypeFordEnum.FORD_FLEET_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.INTERNAL.FLEET');
				break;

			case SalesTypeFordEnum.FORD_GO_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.INTERNAL.FORDGO');
				break;

			case SalesTypeFordEnum.TELEMARKETING_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.INTERNAL.TELEMARKETING');
				break;
		}

		return saleType;
	}

	private isEmail(search: string): boolean {

		const regexp = new RegExp(/^(([^<>()[]\\.,;:\s@"]+(\.[^<>()[]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/);
		return regexp.test(search);
	}
	private isValidEmailFormat(email: string): boolean {
		const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return regex.test(email);
	}
	validateEditForm(): boolean {

		let isValid = true;

		if (this.isEmail(this.filterContractSearchResult.customerEmail) === false) {
			this.messageService.add({
				severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.INVALID.EMAIL.PLEASE.FILL.CORRECTLY')
			});
			isValid = false;
		}
		else if (this.filterContractSearchResult.contractRemarks === "" || this.filterContractSearchResult.contractRemarks === null) {
			this.messageService.add({
				severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACT.REMARKS.FIELD.REQUIRED')
			});
			isValid = false;
		}

		return isValid;
	}

	changeDecimalPoint() {
		if (!this.filterContractSearchResult) {
			return;
		}

		if (this.filterContractSearchResult.parcelValue !== null && this.filterContractSearchResult.parcelValue !== undefined) {
			this.filterContractSearchResult.parcelValue = this.filterContractSearchResult.parcelValue.toString().replace(".", ",");
		}

		if (this.filterContractSearchResult.contractValue !== null && this.filterContractSearchResult.contractValue !== undefined) {
			this.filterContractSearchResult.contractValue = this.filterContractSearchResult.contractValue.toString().replace(".", ",");
		}
	}

	paymentMethodText() {
		let text = this.filterContractSearchResult.paymentMethod;
		if (text === 'CREDIT_CARD') {
			text = "CARTÃO DE CRÉDITO";
		}
		this.filterContractSearchResult.paymentMethod = text;
	}

	restrictCoverageVisitLimitKeypress(event: KeyboardEvent): void {
		const inputElement = event.target as HTMLInputElement;
		if (!inputElement) return;

		// Allow: backspace, delete, tab, escape, enter
		if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
			// Allow: Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A
			(event.keyCode === 65 && event.ctrlKey === true) ||
			(event.keyCode === 67 && event.ctrlKey === true) ||
			(event.keyCode === 86 && event.ctrlKey === true) ||
			(event.keyCode === 88 && event.ctrlKey === true)) {
			return;
		}

		// Only allow digits (0-9)
		if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
			event.preventDefault();
			return;
		}

		// Extract only digits from current value
		const currentDigits = inputElement.value.replace(/[^0-9]/g, '');

		// If already has 2 digits (max for 99), prevent additional digits
		if (currentDigits.length >= 2) {
			event.preventDefault();
			return;
		}
	}

}